import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { Registration } from '../../../types/registration';
import { getNextInvoiceNumber } from '../../../utils/invoiceUtils';
import {
  getCampYear,
  updateCurrentYearAccommodationHistory
} from '../../../utils/accommodationHistoryUtils';

interface HoldReservationCardProps {
  registration: Registration;
  onRegistrationUpdate: (updatedRegistration: Registration) => void;
}

export interface HoldReservationCardRef {
  resetSteps: () => void;
}

const HoldReservationCard = forwardRef<HoldReservationCardRef, HoldReservationCardProps>(({
  registration,
  onRegistrationUpdate
}, ref) => {
  const [step1Complete, setStep1Complete] = useState(false);
  const [step2Complete, setStep2Complete] = useState(false);
  const [step3Complete, setStep3Complete] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Check if steps are already complete based on current state
  useEffect(() => {
    const checkStepsCompletion = async () => {
      if (!registration) return;

      // Step 2: Check if status is Hold or Assigned
      if (registration.status === 'Hold' || registration.status === 'Assigned') {
        setStep2Complete(true);
      } else {
        setStep2Complete(false);
      }

      // Step 3: Check if invoice number exists
      if (registration.invoiceNumber && registration.invoiceNumber.trim() !== '') {
        setStep3Complete(true);
      } else {
        setStep3Complete(false);
      }

      // Step 1: Check if accommodation is occupied
      const assignedAccommodation = registration?.financial?.accommodationFees?.assignedAccommodation;
      if (assignedAccommodation) {
        const inventoryRef = doc(db, 'inventory', 'accommodations');
        const inventorySnap = await getDoc(inventoryRef);
        
        if (inventorySnap.exists()) {
          const inventoryData = inventorySnap.data();
          const accommodationTypes = ['Apartment', 'Cottage', 'Fireside', 'LowerCourt', 'UpperCourt', 'dormA', 'dormB', 'privateCottage'];
          
          let found = false;
          for (const type of accommodationTypes) {
            if (inventoryData[type] && Array.isArray(inventoryData[type])) {
              const accommodations = inventoryData[type];
              const accommodation = accommodations.find((item: any) => 
                item.placeCode && item.placeCode.toLowerCase() === assignedAccommodation.toLowerCase()
              );
              
              if (accommodation) {
                if (accommodation.status === 'occupied') {
                  setStep1Complete(true);
                } else {
                  setStep1Complete(false);
                }
                found = true;
                break;
              }
            }
          }
          if (!found) {
            setStep1Complete(false);
          }
        }
      } else {
        setStep1Complete(false);
      }
    };

    checkStepsCompletion();
  }, [registration]);

  useImperativeHandle(ref, () => ({
    resetSteps: () => {
      setStep1Complete(false);
      setStep2Complete(false);
      setStep3Complete(false);
    }
  }));

  const handleHoldReservation = async () => {
    if (processing) return;
    
    // Reset steps
    setStep1Complete(false);
    setStep2Complete(false);
    setStep3Complete(false);
    setProcessing(true);

    try {
      // Step 1: Mark accommodation as occupied
      const assignedAccommodation = registration?.financial?.accommodationFees?.assignedAccommodation;
      
      if (!assignedAccommodation) {
        alert('No accommodation assigned. Please assign an accommodation first.');
        setProcessing(false);
        return;
      }

      // Update inventory
      const inventoryRef = doc(db, 'inventory', 'accommodations');
      const inventorySnap = await getDoc(inventoryRef);
      
      if (inventorySnap.exists()) {
        const inventoryData = inventorySnap.data();
        const accommodationTypes = ['Apartment', 'Cottage', 'Fireside', 'LowerCourt', 'UpperCourt', 'dormA', 'dormB', 'privateCottage'];
        
        let updated = false;
        for (const type of accommodationTypes) {
          if (inventoryData[type] && Array.isArray(inventoryData[type])) {
            const accommodations = inventoryData[type];
            const index = accommodations.findIndex((item: any) => 
              item.placeCode && item.placeCode.toLowerCase() === assignedAccommodation.toLowerCase()
            );
            
            if (index !== -1) {
              accommodations[index].status = 'occupied';
              await updateDoc(inventoryRef, { [type]: accommodations });
              updated = true;
              break;
            }
          }
        }
        
        if (!updated) {
          alert('Accommodation not found in inventory.');
          setProcessing(false);
          return;
        }
      }
      
      const primaryCamperId = registration?.registrationInfo?.primaryCamperId;
      if (primaryCamperId) {
        const campYear = await getCampYear();
        await updateCurrentYearAccommodationHistory(
          primaryCamperId,
          assignedAccommodation,
          campYear
        );
      } else {
        console.warn('Hold reservation: primary camper ID missing; stay history not updated.');
      }

      setStep1Complete(true);
      await new Promise(resolve => setTimeout(resolve, 500)); // Brief pause for visual feedback

      // Step 2: Set registration status to Hold
      const registrationRef = doc(db, 'Registrations', registration.id);
      await updateDoc(registrationRef, {
        status: 'Hold'
      });
      
      setStep2Complete(true);
      await new Promise(resolve => setTimeout(resolve, 500)); // Brief pause for visual feedback

      // Step 3: Generate invoice number
      const invoiceNumber = await getNextInvoiceNumber();
      await updateDoc(registrationRef, {
        invoiceNumber: invoiceNumber
      });
      
      setStep3Complete(true);

      // Update local state
      const updatedRegistration = {
        ...registration,
        status: 'Hold',
        invoiceNumber: invoiceNumber
      };
      onRegistrationUpdate(updatedRegistration);
    } catch (error) {
      console.error('Error holding reservation:', error);
      alert('Failed to hold reservation. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const canHold = registration?.financial?.accommodationFees?.assignedAccommodation && 
                  registration?.status !== 'Hold';

  return (
    <div className="card mb-3" style={{ borderColor: '#007bff', borderWidth: '2px' }}>
      <div className="card-body p-3">
        <h6 className="card-title mb-3" style={{ fontWeight: 'bold', color: '#28a745', fontSize: '1.1rem' }}>
          Hold Reservation
        </h6>

        <div style={{ marginBottom: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
            <div style={{
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              backgroundColor: step1Complete ? '#28a745' : '#fff',
              border: '2px solid ' + (step1Complete ? '#28a745' : '#dee2e6'),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.75rem',
              color: '#fff',
              fontWeight: 'bold'
            }}>
              {step1Complete && '✓'}
            </div>
            <span style={{ fontSize: '0.9rem', color: '#333' }}>
              Mark accommodation as occupied
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
            <div style={{
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              backgroundColor: step2Complete ? '#28a745' : '#fff',
              border: '2px solid ' + (step2Complete ? '#28a745' : '#dee2e6'),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.75rem',
              color: '#fff',
              fontWeight: 'bold'
            }}>
              {step2Complete && '✓'}
            </div>
            <span style={{ fontSize: '0.9rem', color: '#333' }}>
              Set registration status to Hold
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{
              width: '24px',
              height: '24px',
              borderRadius: '50%',
              backgroundColor: step3Complete ? '#28a745' : '#fff',
              border: '2px solid ' + (step3Complete ? '#28a745' : '#dee2e6'),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.75rem',
              color: '#fff',
              fontWeight: 'bold'
            }}>
              {step3Complete && '✓'}
            </div>
            <span style={{ fontSize: '0.9rem', color: '#333' }}>
              Generate invoice number
            </span>
          </div>
        </div>

        <button
          onClick={handleHoldReservation}
          disabled={!canHold || processing}
          style={{
            width: '100%',
            padding: '0.75rem',
            backgroundColor: canHold && !processing ? '#ffd700' : '#dee2e6',
            color: canHold && !processing ? '#000' : '#6c757d',
            border: 'none',
            borderRadius: '4px',
            cursor: canHold && !processing ? 'pointer' : 'not-allowed',
            fontSize: '1rem',
            fontWeight: 'bold'
          }}
        >
          {processing ? 'Processing...' : 'Hold Reservation'}
        </button>

        {!canHold && registration?.status === 'Hold' && (
          <p className="text-muted mt-2" style={{ fontSize: '0.85rem', marginBottom: 0 }}>
            Reservation is already on hold
          </p>
        )}
        {!canHold && !registration?.financial?.accommodationFees?.assignedAccommodation && (
          <p className="text-muted mt-2" style={{ fontSize: '0.85rem', marginBottom: 0 }}>
            Please assign accommodation first
          </p>
        )}
      </div>
    </div>
  );
});

HoldReservationCard.displayName = 'HoldReservationCard';

export default HoldReservationCard;

